import { formatBodyWeight, formatResultWeight } from '~/utils/weight';
import { ContextMenu } from '~/components/ContextMenu/ContextMenu';
import { useAuthentication } from '~/hooks/useAuthentication';
import { useDeleteResultsMutation } from '../mutations';
import { Heading } from '~/components/Heading/Heading';
import { Table } from '~/components/Table/Table';
import { Fragment } from 'react/jsx-runtime';
import { getRank } from '~/utils/rank';
import { Result } from '~/apis/ssf';

interface Props {
  results: Result[];
  isLoading: boolean;
  heading: string;
}

function Row({ results, onDelete, index }: { results: Result; onDelete: (id: number) => void; index: number }) {
  const { isSuperUser } = useAuthentication();

  return (
    <Table.Row>
      <Table.Col>{`${results.person.first_name} ${results.person.last_name}`}</Table.Col>
      <Table.Col>{results.club.name}</Table.Col>
      <Table.Col>{results.group.name}</Table.Col>
      <Table.Col>{results.age_category.label}</Table.Col>
      <Table.Col>{results.competition_type.description}</Table.Col>
      <Table.Col>{results.weight_class.name}</Table.Col>
      <Table.Col>{formatBodyWeight(results.body_weight)}</Table.Col>
      <Table.Col>{formatResultWeight(results.squat ?? 0)}</Table.Col>
      <Table.Col>{formatResultWeight(results.benchpress ?? 0)}</Table.Col>
      <Table.Col>{formatResultWeight(results.deadlift ?? 0)}</Table.Col>
      <Table.Col>{formatResultWeight(results.total)}</Table.Col>
      <Table.Col>{results.lot}</Table.Col>
      {isSuperUser && <Table.Col>{getRank(results, index)}</Table.Col>}
      <Table.Col align="right">
        <Table.Context
          items={[<ContextMenu.Item label="Ta bort" key="delete" onClick={() => onDelete(results.id)} danger />]}
        />
      </Table.Col>
    </Table.Row>
  );
}

export function ResultTable({ results, isLoading, heading }: Props) {
  const { isSuperUser } = useAuthentication();
  const deleteMutation = useDeleteResultsMutation();

  async function handleDelete(id: number) {
    await deleteMutation.mutateAsync(id);
  }

  return (
    <Fragment>
      <Heading as="h5" style={{ marginBottom: 'var(--size-3)' }}>
        {heading}
      </Heading>
      <Table variant="plain">
        <Table.Head>
          <Table.Row>
            <Table.Col as="th">Namn</Table.Col>
            <Table.Col as="th">Förening</Table.Col>
            <Table.Col as="th">Grupp</Table.Col>
            <Table.Col as="th">Ålderskategori</Table.Col>
            <Table.Col as="th">Tävlingstyp</Table.Col>
            <Table.Col as="th">Viktklass</Table.Col>
            <Table.Col as="th">Kroppsvikt</Table.Col>
            <Table.Col as="th">Knäböj</Table.Col>
            <Table.Col as="th">Bänkpress</Table.Col>
            <Table.Col as="th">Marklyft</Table.Col>
            <Table.Col as="th">Total</Table.Col>
            <Table.Col as="th">Lott</Table.Col>
            {isSuperUser && <Table.Col as="th">Placering</Table.Col>}
            <Table.Col as="th" />
          </Table.Row>
        </Table.Head>
        <Table.Body>
          {isLoading ? (
            <Table.Loading cols={12} />
          ) : (
            results.map((results, index) => (
              <Row results={results} key={results.id} onDelete={handleDelete} index={index} />
            ))
          )}
        </Table.Body>
      </Table>
    </Fragment>
  );
}
